import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '@/lib/api';
import { clearAuthStorage } from '@/lib/clearAuthStorage';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
  emailVerified?: boolean;
}

interface RegisterData {
  organizationName: string;
  name: string;
  email: string;
  password: string;
  phone?: string;
  country?: string;
  // Honeypot — hidden signup field; only bots fill it.
  website?: string;
}

// Per-module permission map fetched from /roles/me. null = not yet loaded
// or no Role row matched (fall back to legacy role-based gating).
export type PermissionMap = Record<string, Record<string, boolean>>;

// One AppModule row from the tenant's plan — drives the DB-rendered
// sidebar (label, icon, href, hierarchy) instead of a hardcoded array.
export interface PlanModule {
  id: string;
  name: string;
  pageLink: string;
  icon: string;            // RemixIcon class, e.g. 'ri-dashboard-line'
  type: string;            // 'M' = main, 'S' = sub
  parentId: string | null;
  orderNumber: number;
}

// Slim brand object the sidebar / dashboard header reads to swap the
// "BrixLead" wordmark with the tenant logo + name.
export interface TenantBrand {
  organizationName: string | null;
  logoSquare: string | null;
  logoRectangular: string | null;
  primaryColor: string | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  permissions: PermissionMap | null;
  permissionsLoaded: boolean;
  // Module names included in the current tenant's plan — hard upper
  // bound on sidebar visibility + Roles permission grid options.
  planModules: string[] | null;
  // Full module catalog (mains + subs) for the tenant's plan — the
  // sidebar renders 100% from this instead of a hardcoded list.
  planMenu: PlanModule[] | null;
  planName: string | null;
  brand: TenantBrand | null;
  refreshBrand: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  setEmailVerified: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const PERMS_CACHE_KEY = 'auth:permissions';
const PLAN_MODS_CACHE_KEY = 'auth:planModules';
const PLAN_MENU_CACHE_KEY = 'auth:planMenu';
const PLAN_NAME_CACHE_KEY = 'auth:planName';
const BRAND_CACHE_KEY = 'auth:brand';

export function AuthProvider({ children }: { children: ReactNode }) {
  // Lazy initialisers — read localStorage SYNCHRONOUSLY on the very first
  // render, before React paints anything. Previously this hydration was
  // inside a useEffect, so the first paint always showed defaults
  // ("BrixLead" wordmark, no permissions, no plan modules) before flipping
  // to the cached tenant values on the next tick. Visible flash on every
  // page reload.
  //
  // Each helper is tolerant of corrupt JSON — clear the bad key + return
  // null so the next API refresh can re-hydrate cleanly.
  const readCachedJson = <T,>(key: string): T | null => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  };

  const [user, setUser] = useState<User | null>(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    const parsed = readCachedJson<User>('user');
    if (!parsed) clearAuthStorage();
    return parsed;
  });
  // isLoading still flips after the (now-empty) effect runs — preserves
  // the existing contract that consumers can wait for hydration before
  // hard-redirecting on auth state.
  const [isLoading, setIsLoading] = useState(true);
  // Permissions are intentionally NOT hydrated from cache. Painting the
  // sidebar from a stale permission snapshot made a since-removed module
  // flash visible for a split-second before the fresh /roles/me (or login)
  // perms hid it. Starting null keeps gated items hidden (default-deny) until
  // authoritative perms arrive — admins still see everything instantly via
  // their role, non-admins get the gated menu a moment later. No forbidden
  // flash. planMenu/brand stay cached below for instant structure + logo.
  const [permissions, setPermissions] = useState<PermissionMap | null>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false);
  const [planModules, setPlanModules] = useState<string[] | null>(
    () => readCachedJson<string[]>(PLAN_MODS_CACHE_KEY),
  );
  const [planMenu, setPlanMenu] = useState<PlanModule[] | null>(
    () => readCachedJson<PlanModule[]>(PLAN_MENU_CACHE_KEY),
  );
  const [planName, setPlanName] = useState<string | null>(
    () => localStorage.getItem(PLAN_NAME_CACHE_KEY),
  );
  const [brand, setBrand] = useState<TenantBrand | null>(
    () => readCachedJson<TenantBrand>(BRAND_CACHE_KEY),
  );

  useEffect(() => {
    // Hydration already happened above via lazy initialisers. This effect
    // just clears the loading flag so route guards can stop blocking. The
    // background refresh of perms / plan / brand fires from the user-watch
    // effect below.
    setIsLoading(false);
  }, []);

  // Pull a fresh brand snapshot — called after the SettingsPage saves
  // brand changes so the sidebar logo + name update without a full
  // page reload. Cache hydrate keeps subsequent renders instant.
  const refreshBrand = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/me/brand');
      const b: TenantBrand = {
        organizationName: data?.organizationName ?? null,
        logoSquare: data?.logoSquare ?? null,
        logoRectangular: data?.logoRectangular ?? null,
        primaryColor: data?.primaryColor ?? null,
      };
      setBrand(b);
      localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(b));
    } catch {
      // Leave existing state alone — don't blank out the sidebar on a
      // transient fetch failure.
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    try {
      const [permsRes, planRes] = await Promise.allSettled([
        api.get('/roles/me'),
        api.get('/tenant/me/plan-modules'),
      ]);
      if (permsRes.status === 'fulfilled') {
        const perms: PermissionMap | null = permsRes.value.data?.permissions ?? null;
        setPermissions(perms);
        if (perms) localStorage.setItem(PERMS_CACHE_KEY, JSON.stringify(perms));
        else localStorage.removeItem(PERMS_CACHE_KEY);
      }
      if (planRes.status === 'fulfilled') {
        const mods: string[] = Array.isArray(planRes.value.data?.moduleNames) ? planRes.value.data.moduleNames : [];
        const menu: PlanModule[] = Array.isArray(planRes.value.data?.modules) ? planRes.value.data.modules : [];
        const pname: string | null = planRes.value.data?.planName ?? null;
        setPlanModules(mods);
        setPlanMenu(menu);
        setPlanName(pname);
        localStorage.setItem(PLAN_MODS_CACHE_KEY, JSON.stringify(mods));
        localStorage.setItem(PLAN_MENU_CACHE_KEY, JSON.stringify(menu));
        if (pname) localStorage.setItem(PLAN_NAME_CACHE_KEY, pname);
        else localStorage.removeItem(PLAN_NAME_CACHE_KEY);
      }
      setPermissionsLoaded(true);
      // Refresh brand in background — independent of perms/plan.
      refreshBrand();
    } catch {
      setPermissionsLoaded(true);
    }
  }, [refreshBrand]);

  // Whenever we know there's a logged-in user, make sure permissions,
  // plan modules and brand are fresh — initial mount and after login.
  useEffect(() => {
    if (user) {
      refreshPermissions();
    } else {
      setPermissions(null);
      setPermissionsLoaded(false);
      setPlanModules(null);
      setPlanMenu(null);
      setPlanName(null);
      setBrand(null);
      localStorage.removeItem(PERMS_CACHE_KEY);
      localStorage.removeItem(PLAN_MODS_CACHE_KEY);
      localStorage.removeItem(PLAN_MENU_CACHE_KEY);
      localStorage.removeItem(PLAN_NAME_CACHE_KEY);
      localStorage.removeItem(BRAND_CACHE_KEY);
    }
  }, [user, refreshPermissions]);

  const storeAuth = (data: any) => {
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('userId', data.user.id);
    const userData = { ...data.user, tenantId: data.tenantId };
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const login = async (email: string, password: string) => {
    const { data } = await api.post('/auth/login', { email, password });
    // Hydrate permissions + plan modules + brand from the login
    // response BEFORE storeAuth so the sidebar paints the correct menu
    // set + tenant logo on the very first render — no flash.
    const perms: PermissionMap | null = data?.permissions ?? null;
    setPermissions(perms);
    setPermissionsLoaded(true);
    if (perms) localStorage.setItem(PERMS_CACHE_KEY, JSON.stringify(perms));
    else localStorage.removeItem(PERMS_CACHE_KEY);

    const mods: string[] = Array.isArray(data?.planModules) ? data.planModules : [];
    const pname: string | null = data?.planName ?? null;
    setPlanModules(mods);
    setPlanName(pname);
    localStorage.setItem(PLAN_MODS_CACHE_KEY, JSON.stringify(mods));
    if (pname) localStorage.setItem(PLAN_NAME_CACHE_KEY, pname);
    else localStorage.removeItem(PLAN_NAME_CACHE_KEY);

    if (data?.brand) {
      const b: TenantBrand = {
        organizationName: data.brand.organizationName ?? null,
        logoSquare: data.brand.logoSquare ?? null,
        logoRectangular: data.brand.logoRectangular ?? null,
        primaryColor: data.brand.primaryColor ?? null,
      };
      setBrand(b);
      localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(b));
    }

    storeAuth(data);
    // If email not verified, store email for verify page
    if (data.user?.emailVerified === false) {
      localStorage.setItem('verifyEmail', email);
    }
  };

  const register = async (formData: RegisterData) => {
    const { data } = await api.post('/auth/register', formData);
    storeAuth(data);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearAuthStorage();
      localStorage.removeItem(PERMS_CACHE_KEY);
      localStorage.removeItem(PLAN_MODS_CACHE_KEY);
      localStorage.removeItem(PLAN_MENU_CACHE_KEY);
      localStorage.removeItem(PLAN_NAME_CACHE_KEY);
      localStorage.removeItem(BRAND_CACHE_KEY);
      setUser(null);
      setPermissions(null);
      setPermissionsLoaded(false);
      setPlanModules(null);
      setPlanMenu(null);
      setPlanName(null);
      setBrand(null);
    }
  };

  const setEmailVerified = () => {
    if (user) {
      const updated = { ...user, emailVerified: true };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      permissions,
      permissionsLoaded,
      planModules,
      planMenu,
      planName,
      brand,
      refreshBrand,
      refreshPermissions,
      login,
      register,
      logout,
      setEmailVerified,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
